import React, { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import {
  deleteOrganization,
  getOrganizationInvoiceFoundations,
  getOrganizationDeletionStatus,
  updateOrganization,
} from "../services/momangAdminService";
import { Button, Loader, Message, Segment, Table } from "semantic-ui-react";
import { DangerZone } from "../components/OrganizationDeletion";
import { EditOrganization } from "../components/EditOrganization";
import { useAuthenticatedUserContext } from "../contexts/authenticatedUserContext";
import { useQuery } from "react-query";
import { createOrganizationFineTuneModel, getProfileAutomatorOrganization } from "../services/profileAutomatorService";
import { AiUsageChart } from "../components/AiUsageChart";
import { getAiMonthlyBudgetStatus, getAiUsagePerFunction } from "../services/analyticsService";

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value) {
  if (value === null || value === undefined) return "Not set";
  return usdFormatter.format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return "Not set";
  return `${value}%`;
}

function getEffectiveBudgetStatus(status, organization) {
  const baseUsd = organization?.aiMonthlyBudgetBaseUsdOverride ?? status.calculatedBaseUsd;
  const perUserUsd = organization?.aiMonthlyBudgetPerUserUsdOverride ?? status.calculatedPerUserUsd;
  const monthlyBudgetUsd = baseUsd + status.activeUserCount * perUserUsd;
  return {
    ...status,
    baseUsd,
    perUserUsd,
    monthlyBudgetUsd,
    budgetExceeded: status.currentMonthSpendUsd >= monthlyBudgetUsd,
  };
}

function BudgetLimitMessage({ budgetExceeded }) {
  return (
    <Message
      positive={!budgetExceeded}
      warning={budgetExceeded}
      content={
        budgetExceeded
          ? "The hard AI budget limit is currently reached. OpenAI calls are blocked for this organization."
          : "The organization is currently below the hard AI budget limit."
      }
    />
  );
}

function AiBudgetStatus({ isLoading, error, status, organization }) {
  if (isLoading) return <Loader active inline="centered" content="Loading AI budget status..." />;
  const effectiveStatus = status && getEffectiveBudgetStatus(status, organization);
  if (!effectiveStatus) return <Message error content={error?.message} hidden={!error} />;

  return (
    <>
      <BudgetLimitMessage budgetExceeded={effectiveStatus.budgetExceeded} />
      <Message error content={error?.message} hidden={!error} />
      <AiBudgetStatusTable status={effectiveStatus} />
    </>
  );
}

function AiBudgetSection({ isLoading, error, status, organization }) {
  return (
    <>
      <h2>AI budget</h2>
      <AiBudgetStatus isLoading={isLoading} error={error} status={status} organization={organization} />
    </>
  );
}

function UpdateSection({ organization, onChange }) {
  return (
    <>
      <h2>Update</h2>
      <EditOrganization organization={organization} onChange={onChange} />
    </>
  );
}

function AiBudgetStatusTable({ status }) {
  return (
    <Table definition>
      <Table.Body>
        <Table.Row>
          <Table.Cell width={5}>Usage month</Table.Cell>
          <Table.Cell>{status.usageMonth}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Current month AI spend</Table.Cell>
          <Table.Cell>{formatUsd(status.currentMonthSpendUsd)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Active user count</Table.Cell>
          <Table.Cell>{status.activeUserCount}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Base</Table.Cell>
          <Table.Cell>{formatUsd(status.baseUsd)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Per-user</Table.Cell>
          <Table.Cell>{formatUsd(status.perUserUsd)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Total monthly budget</Table.Cell>
          <Table.Cell>{formatUsd(status.monthlyBudgetUsd)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Warning threshold</Table.Cell>
          <Table.Cell>{formatPercent(status.warningThresholdPercent)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Hard limit reached</Table.Cell>
          <Table.Cell>{status.budgetExceeded ? "Yes" : "No"}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
}

function GhostSection({ authenticatedUser, organization, organizationSlug, ghost, resurrect }) {
  const isGhostInOrganization =
    authenticatedUser?.roles?.includes("ghost") && authenticatedUser?.organizationSlug === organizationSlug;

  return (
    <>
      <h2>Ghost</h2>
      {isGhostInOrganization ? (
        <>
          <p>
            You are a ghost in <i>{organization?.name}</i>.
          </p>
          <Button content="Resurrect" icon="lightning" onClick={() => resurrect()} />
        </>
      ) : (
        <>
          <p>
            Join this organization as a ghost user. When you log in to Momang, you will belong to{" "}
            <i>{organization?.name}</i>, but you will not be visible in the lists of users and consultants.
          </p>
          <Button
            icon="snapchat ghost"
            onClick={() => ghost(organization?.slug)}
            content={`Join ${organization?.name} as ghost`}
          />
        </>
      )}
    </>
  );
}

function InvoiceSection({ invoiceFoundations }) {
  return (
    <>
      <h2>Invoice</h2>
      <p>
        A user is considered billable in a period as long as it is (a) created before the period starts, (b) not deleted
        before the period starts, and (c) has at least one of the roles 'admin', 'sales' or 'consultant'.
      </p>
      <p>Note that subcontractors are not included.</p>
      <p>
        Note that we don't store historical information on <i>when</i> a user changes role, so the previously periods
        will also change if a user is changed from a subcontractor to a consultant, for example, this will change data
        for previous periods. Similarly, changes in price per user will also affect previous periods.
      </p>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Period</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Billable users</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Discount</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Total</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invoiceFoundations.map((invoiceFoundation, i) => (
            <InvoiceRow invoiceFoundation={invoiceFoundation} key={i} />
          ))}
        </Table.Body>
      </Table>
    </>
  );
}

function InvoiceRow({ invoiceFoundation }) {
  const periodStart = DateTime.fromISO(invoiceFoundation.periodStart);
  const currencyFormatter = new Intl.NumberFormat("sv", {
    style: "currency",
    currency: invoiceFoundation.invoiceCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  });

  return (
    <Table.Row>
      <Table.Cell>{periodStart.toFormat("MMMM yyyy")}</Table.Cell>
      <Table.Cell textAlign="right">{invoiceFoundation.billableUsers}</Table.Cell>
      <Table.Cell textAlign="right">{currencyFormatter.format(invoiceFoundation.invoiceAmount)}</Table.Cell>
      <Table.Cell textAlign="right">{currencyFormatter.format(-invoiceFoundation.invoiceDiscount)}</Table.Cell>
      <Table.Cell textAlign="right">
        <strong>{currencyFormatter.format(invoiceFoundation.invoiceTotal)}</strong>
      </Table.Cell>
    </Table.Row>
  );
}

function ProfileAutomatorSection({ organizationSlug, organization, isLoading, error }) {
  return (
    <>
      <h2>Profile Automator data</h2>
      <p>
        A fine-tune model uses the content already entered into Momang as training data for suggesting new project
        descriptions. This should give more accurate and similar texts to what the user expects. The more training data,
        the better the results. We require 100 existing project descriptions in the consultants' main profiles in order
        to start the fine tune training.
      </p>
      <Button content="Create a fine-tune model" onClick={() => createOrganizationFineTuneModel(organizationSlug)} />
      {isLoading ? (
        <Loader active inline="centered" content="Loading profile automator data..." />
      ) : (
        <Segment>
          <pre>{JSON.stringify(organization, null, 2)}</pre>
          <Message error content={error?.message} hidden={!error} />
        </Segment>
      )}
    </>
  );
}

function useOrganizationInvoiceData(organizationSlug) {
  const [invoiceData, setInvoiceData] = useState();
  const [loading, setLoading] = useState(false);

  const updateInvoiceData = useCallback(async () => {
    if (!organizationSlug) return;
    setLoading(true);
    try {
      setInvoiceData(await getOrganizationInvoiceFoundations(organizationSlug));
    } finally {
      setLoading(false);
    }
  }, [organizationSlug]);

  useEffect(() => {
    updateInvoiceData();
  }, [organizationSlug, updateInvoiceData]);

  return { invoiceData, loading, updateInvoiceData };
}

export function Organization() {
  const { organizationSlug } = useParams();
  const { authenticatedUser, ghost, resurrect } = useAuthenticatedUserContext();
  const { invoiceData, loading, updateInvoiceData } = useOrganizationInvoiceData(organizationSlug);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletionError, setDeletionError] = useState();
  const {
    data: deletion,
    error: statusError,
    refetch: refreshDeletion,
  } = useQuery(["organization-deletion", organizationSlug], () => getOrganizationDeletionStatus(organizationSlug), {
    refetchInterval: (data) => (["queued", "running"].includes(data?.status) ? 3000 : false),
  });

  const {
    isLoading: isLoadingAiBudgetStatus,
    error: errorAiBudgetStatus,
    data: aiBudgetStatus,
    refetch: refetchAiBudgetStatus,
  } = useQuery(["ai-monthly-budget-status", organizationSlug], () => getAiMonthlyBudgetStatus({ organizationSlug }), {
    enabled: !!organizationSlug,
  });

  const {
    isLoading: isLoadingProfileAutomator,
    error: errorProfileAutomator,
    data: profileAutomatorOrganization,
  } = useQuery(["profile-automator-organization", organizationSlug], () =>
    getProfileAutomatorOrganization(organizationSlug)
  );

  async function updateOrganizationSettings(organization) {
    setUpdating(true);
    try {
      await updateOrganization(organization);
      await updateInvoiceData();
      await refetchAiBudgetStatus();
    } finally {
      setUpdating(false);
    }
  }

  async function deleteCurrentOrganization() {
    if (window.confirm(`Are you sure you want to delete ${invoiceData.organization?.name}?`)) {
      setDeleting(true);
      setDeletionError(undefined);
      try {
        await deleteOrganization(invoiceData.organization?.slug);
      } catch (error) {
        setDeletionError(error);
      } finally {
        await refreshDeletion();
        setDeleting(false);
      }
    }
  }

  if (!invoiceData) return <Loader active />;

  return (
    <Segment basic disabled={loading || updating}>
      <h1>{invoiceData.organization?.name}</h1>
      <GhostSection
        authenticatedUser={authenticatedUser}
        organization={invoiceData.organization}
        organizationSlug={organizationSlug}
        ghost={ghost}
        resurrect={resurrect}
      />

      <UpdateSection organization={invoiceData.organization} onChange={updateOrganizationSettings} />

      <AiBudgetSection
        isLoading={isLoadingAiBudgetStatus}
        error={errorAiBudgetStatus}
        status={aiBudgetStatus}
        organization={invoiceData.organization}
      />

      <h2>AI usage</h2>
      <AiUsageChart
        organizationSlug={organizationSlug}
        fetcher={getAiUsagePerFunction}
        fetcherDependencies={["ai-usage-per-function", organizationSlug]}
        showLegend
      />

      <InvoiceSection invoiceFoundations={invoiceData.invoiceFoundations} />
      <h2>Organization data</h2>
      <Segment>
        <pre>{JSON.stringify(invoiceData.organization, null, 2)}</pre>
      </Segment>

      <ProfileAutomatorSection
        organizationSlug={organizationSlug}
        organization={profileAutomatorOrganization}
        isLoading={isLoadingProfileAutomator}
        error={errorProfileAutomator}
      />

      <DangerZone
        organization={invoiceData.organization}
        onDelete={deleteCurrentOrganization}
        deletion={deletion}
        deleting={deleting}
        error={deletionError || statusError}
      />
    </Segment>
  );
}
